import React, { useState, useEffect } from 'react';
import { RANKS } from '../constants/ranks';
import { getRank, compositeScore } from '../utils/helpers';
import { fetchGamesBySeason, fetchSeasonSnapshots, updateUserClaimedPlayer, getAllClaimedPlayers } from '../utils/firebase';
import { db } from '../firebase';
import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { calculateNetWorth, getRichnessRank, RICHNESS_RANKS, LOOTBOX_LABELS } from '../constants/richness';
import { STOCKS } from '../constants/stocks';
import PlayerDetailModal from './PlayerDetailModal';
import EloDetailsModal from './EloDetailsModal';
import PlayerNameWithEmoji from './PlayerNameWithEmoji';
import { useToast } from './ToastContext';
import { STATTRAK_LABELS } from '../constants/lootbox';
import { PgHeader, SubTabs } from './ui';

function LeaderboardPage({ players, onPlayersChange, user, userData, onUserDataChange, playerEmojis = {}, seasonConfig }) {
  const toast = useToast();
  const [tab, setTab] = useState("overall");
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [games, setGames] = useState([]);
  const [loadingGames, setLoadingGames] = useState(false);
  const [sortBy, setSortBy] = useState("elo");
  const [sortDir, setSortDir] = useState("desc");
  const [claimedPlayers, setClaimedPlayers] = useState([]);
  const [selectedGameForElo, setSelectedGameForElo] = useState(null);
  const [richnessData, setRichnessData] = useState([]);
  const [richnessLoading, setRichnessLoading] = useState(false);
  const [richnessSortBy, setRichnessSortBy] = useState("netWorth");
  const [richnessSortDir, setRichnessSortDir] = useState("desc");
  const [selectedSeason, setSelectedSeason] = useState(null); // null = current season
  const [seasonSnapshotMap, setSeasonSnapshotMap] = useState({}); // playerId → snapshot data

  const currentSeason = seasonConfig?.currentSeason || 1;
  const seasons = seasonConfig?.seasons || { '1': { label: 'Season 1' } };
  const viewingSeason = selectedSeason ?? currentSeason;
  const isViewingPastSeason = viewingSeason !== currentSeason;

  const tabs = [
    { id: "overall", label: "🏆 Overall" },
    { id: "imposter", label: "🔪 Imposter" },
    { id: "crewmate", label: "🔵 Crewmate" },
    { id: "gamelog", label: "📋 Game Log" },
    { id: "richness", label: "💰 Richness" },
  ];

  async function loadGames(season) {
    setLoadingGames(true);
    const effective = season ?? currentSeason;
    // For season 1, fetch all games (old games lack season field)
    const effectiveSeason = effective <= 1 ? null : effective;
    const fetched = await fetchGamesBySeason(effectiveSeason);
    setGames(fetched);

    // Load season snapshots for past seasons
    if (effective !== currentSeason) {
      const snapshots = await fetchSeasonSnapshots(effective);
      const map = {};
      for (const snap of snapshots) {
        map[snap.playerId] = snap;
      }
      setSeasonSnapshotMap(map);
    } else {
      setSeasonSnapshotMap({});
    }

    setLoadingGames(false);
  }

  // Calculate streak for a player based on games
  // role: 'overall', 'imposter', or 'crewmate'
  function calculateStreak(playerId, allGames, role = 'overall') {
    let playerGames = allGames
      .filter(g =>
        g.impostors.includes(playerId) ||
        (g.crewmates && g.crewmates.includes(playerId))
      );

    // Filter by role if specified
    if (role === 'imposter') {
      playerGames = playerGames.filter(g => g.impostors.includes(playerId));
    } else if (role === 'crewmate') {
      playerGames = playerGames.filter(g =>
        (g.crewmates && g.crewmates.includes(playerId)) ||
        (!g.impostors.includes(playerId) && !g.crewmates)
      );
    }

    // Sort newest first
    playerGames = playerGames.sort((a, b) => b.timestamp - a.timestamp);

    if (playerGames.length === 0) return { streak: 0, type: 'none' };

    let streak = 0;
    let lastResult = null;

    // Iterate from MOST RECENT to oldest to find current streak
    for (const game of playerGames) {
      const isImposter = game.impostors.includes(playerId);
      const won = (isImposter && game.didWin) || (!isImposter && !game.didWin);

      if (lastResult === null) {
        lastResult = won;
        streak = 1;
      } else if (lastResult === won) {
        streak++;
      } else {
        // Different result, streak ends
        break;
      }
    }

    return { streak, type: lastResult ? 'win' : 'loss' };
  }

  // Find a player's playerStats entry in a game
  function findPlayerStats(game, playerId) {
    if (!game.playerStats || !Array.isArray(game.playerStats)) return null;
    if (game.eloChanges && Array.isArray(game.eloChanges)) {
      const eloEntry = game.eloChanges.find(e => e.id === playerId);
      if (eloEntry) {
        const ps = game.playerStats.find(p => p.playerName === eloEntry.name);
        if (ps) return ps;
      }
    }
    const byId = game.playerStats.find(p => p.playerId === playerId);
    if (byId) return byId;
    return game.playerStats.find(p => p.playerName && p.playerName.toLowerCase() === playerId.toLowerCase()) || null;
  }

  // Count games where the player was alive at end of round
  function countSurvivedGames(playerGames, playerId) {
    return playerGames.filter(g => {
      const ps = findPlayerStats(g, playerId);
      return ps && ps.isAlive === true;
    }).length;
  }

  // Get players with calculated stats for the current tab
  // When viewing a past season, use snapshot ELO values instead of live data
  function getPlayersWithStats() {
    if (tab === "gamelog" || tab === "richness") return [];

    return players.map(p => {
      // For past seasons, overlay snapshot ELO onto the player object
      const snap = isViewingPastSeason ? seasonSnapshotMap[p.id] : null;
      const effectivePlayer = snap ? {
        ...p,
        eloImposter: snap.eloImposter || 1100,
        eloCrewmate: snap.eloCrewmate || 1100,
        gameScore: snap.gameScore || ((snap.eloImposter || 1100) + (snap.eloCrewmate || 1100)) / 2,
      } : p;

      if (tab === "overall") {
        const streakData = calculateStreak(p.id, games, 'overall');

        // Calculate actual game count from games collection
        const playerGames = games.filter(g =>
          g.impostors.includes(p.id) ||
          (g.crewmates && g.crewmates.includes(p.id))
        );
        const actualGameCount = playerGames.length;
        const elo = compositeScore(effectivePlayer, actualGameCount);

        // Calculate actual wins from game results
        const winGames = playerGames.filter(g => {
          const isImposter = g.impostors.includes(p.id);
          return (isImposter && g.didWin) || (!isImposter && !g.didWin);
        });
        const lossGames = playerGames.filter(g => {
          const isImposter = g.impostors.includes(p.id);
          return !((isImposter && g.didWin) || (!isImposter && !g.didWin));
        });
        const actualWins = winGames.length;
        const losses = lossGames.length;
        const winRate = actualGameCount > 0 ? ((actualWins / actualGameCount) * 100).toFixed(1) : "0.0";

        return {
          ...p,
          elo,
          wins: actualWins,
          losses,
          survivedWins: countSurvivedGames(winGames, p.id),
          survivedLosses: countSurvivedGames(lossGames, p.id),
          games: actualGameCount,
          winRate: parseFloat(winRate),
          streak: streakData.streak,
          streakType: streakData.type,
        };
      } else if (tab === "imposter") {
        const streakData = calculateStreak(p.id, games, 'imposter');
        // Get imposter games only
        const imposterGames = games.filter(g => g.impostors.includes(p.id));
        const winGames = imposterGames.filter(g => g.didWin);
        const lossGames = imposterGames.filter(g => !g.didWin);
        const winRate = imposterGames.length > 0 ? ((winGames.length / imposterGames.length) * 100).toFixed(1) : "0.0";

        return {
          ...p,
          elo: Math.round(effectivePlayer.eloImposter),
          wins: winGames.length,
          losses: lossGames.length,
          survivedWins: countSurvivedGames(winGames, p.id),
          survivedLosses: countSurvivedGames(lossGames, p.id),
          games: imposterGames.length,
          winRate: parseFloat(winRate),
          streak: streakData.streak,
          streakType: streakData.type,
        };
      } else { // crewmate
        const streakData = calculateStreak(p.id, games, 'crewmate');
        // Get crewmate games only
        const crewmateGames = games.filter(g =>
          (g.crewmates && g.crewmates.includes(p.id)) ||
          (!g.impostors.includes(p.id) && !g.crewmates)
        );
        const winGames = crewmateGames.filter(g => !g.didWin);
        const lossGames = crewmateGames.filter(g => g.didWin);
        const winRate = crewmateGames.length > 0 ? ((winGames.length / crewmateGames.length) * 100).toFixed(1) : "0.0";

        return {
          ...p,
          elo: Math.round(effectivePlayer.eloCrewmate),
          wins: winGames.length,
          losses: lossGames.length,
          survivedWins: countSurvivedGames(winGames, p.id),
          survivedLosses: countSurvivedGames(lossGames, p.id),
          games: crewmateGames.length,
          winRate: parseFloat(winRate),
          streak: streakData.streak,
          streakType: streakData.type,
        };
      }
    }).filter(p => p.elo !== null && p.elo !== undefined);
  }

  const playersWithStats = getPlayersWithStats();

  // Sort players based on current sort settings
  function handleSort(column) {
    if (sortBy === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDir(column === "name" ? "asc" : "desc");
    }
  }

  const sortedPlayers = [...playersWithStats].sort((a, b) => {
    let aVal = a[sortBy];
    let bVal = b[sortBy];

    if (sortBy === "name") {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
      return sortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }

    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    }

    return 0;
  });

  // Sort richness data
  function handleRichnessSort(column) {
    if (richnessSortBy === column) {
      setRichnessSortDir(richnessSortDir === "asc" ? "desc" : "asc");
    } else {
      setRichnessSortBy(column);
      setRichnessSortDir(column === "name" ? "asc" : "desc");
    }
  }

  const sortedRichness = [...richnessData].sort((a, b) => {
    let aVal = richnessSortBy === "name" ? a.player.name : a[richnessSortBy];
    let bVal = richnessSortBy === "name" ? b.player.name : b[richnessSortBy];
    if (richnessSortBy === "name") {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
      return richnessSortDir === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    if (typeof aVal === "number" && typeof bVal === "number") {
      return richnessSortDir === "asc" ? aVal - bVal : bVal - aVal;
    }
    return 0;
  });

  function RichnessHeader({ column, label }) {
    const isActive = richnessSortBy === column;
    return (
      <th className="lb-th" onClick={() => handleRichnessSort(column)}>
        <div className="lb-th-content">
          {label}
          <span className={`sort-indicator ${isActive ? "active" : ""}`}>
            {isActive && richnessSortDir === "asc" ? "▲" : "▼"}
          </span>
        </div>
      </th>
    );
  }

  function formatDate(timestamp) {
    return new Date(timestamp).toLocaleString();
  }

  async function handleClaimPlayer(playerId) {
    if (!user) {
      toast.error("Please log in to claim a player.");
      return;
    }

    if (userData && userData.claimedPlayer) {
      toast.warn("You have already claimed a player. You can only claim one player.");
      return;
    }

    // Check if player is already claimed
    if (claimedPlayers.includes(playerId)) {
      toast.warn("This player has already been claimed by another user.");
      return;
    }

    await updateUserClaimedPlayer(user.uid, playerId);
    if (onUserDataChange) {
      onUserDataChange();
    }
    // Reload claimed players
    loadClaimedPlayers();
  }

  async function loadClaimedPlayers() {
    const claimed = await getAllClaimedPlayers();
    setClaimedPlayers(claimed);
  }

  // Load games on mount and when season changes
  React.useEffect(() => {
    if (!user) return;
    loadGames(selectedSeason);
    loadClaimedPlayers();
  }, [user, selectedSeason]); // eslint-disable-line react-hooks/exhaustive-deps

  // Reset sort when changing tabs
  React.useEffect(() => {
    setSortBy("elo");
    setSortDir("desc");
  }, [tab]);


  // Load richness data when tab selected
  useEffect(() => {
    if (tab !== 'richness' || richnessData.length > 0) return;
    async function loadRichness() {
      setRichnessLoading(true);

      // Fetch current stock prices and all holdings for live portfolio calculation
      const [usersSnap, priceSnap, holdingsSnap] = await Promise.all([
        getDocs(collection(db, "users")),
        getDoc(doc(db, 'stockPrices', 'current')),
        getDocs(collection(db, 'stockHoldings')),
      ]);

      const stockPrices = priceSnap.exists() ? priceSnap.data().prices : {};
      const holdingsMap = {};
      holdingsSnap.docs.forEach(d => { holdingsMap[d.id] = d.data().holdings || {}; });

      const results = [];
      usersSnap.docs.forEach(d => {
        const data = d.data();
        if (data.claimedPlayer && data.email !== 'test@test.com') {
          const player = players.find(p => p.id === data.claimedPlayer);
          if (player) {
            // Calculate live stock portfolio value from current prices
            let liveStockValue = 0;
            const userHoldings = holdingsMap[d.id] || {};
            for (const [stockId, holding] of Object.entries(userHoldings)) {
              if (holding.shares > 0) {
                const stockDef = STOCKS.find(s => s.id === stockId);
                if (stockDef && stockPrices[stockDef.ticker]) {
                  liveStockValue += holding.shares * stockPrices[stockDef.ticker].price;
                }
              }
            }
            liveStockValue = parseFloat(liveStockValue.toFixed(4));

            // Use live stock value instead of cached stockPortfolioValue
            const dataWithLiveStocks = { ...data, stockPortfolioValue: liveStockValue };
            const worth = calculateNetWorth(dataWithLiveStocks, true);
            results.push({
              player,
              crewmateTokens: data.crewmateTokens || 0,
              imposterTokens: data.imposterTokens || 0,
              liquid: worth.liquid,
              lootboxes: worth.lootboxes,
              lootboxCounts: worth.lootboxCounts,
              items: worth.items,
              itemCount: worth.itemCount,
              stocks: liveStockValue,
              assets: (worth.lootboxes || 0) + liveStockValue,
              netWorth: worth.total,
              rank: getRichnessRank(worth.total),
            });
          }
        }
      });
      results.sort((a, b) => b.netWorth - a.netWorth);
      setRichnessData(results);
      setRichnessLoading(false);
    }
    loadRichness();
  }, [tab, players]); // eslint-disable-line react-hooks/exhaustive-deps

  // Render sortable table header
  function TableHeader({ column, label }) {
    const isActive = sortBy === column;
    return (
      <th className="lb-th" onClick={() => handleSort(column)}>
        <div className="lb-th-content">
          {label}
          <span className={`sort-indicator ${isActive ? "active" : ""}`}>
            {isActive && sortDir === "asc" ? "▲" : "▼"}
          </span>
        </div>
      </th>
    );
  }

  // Check if it's Double Token Weekend (Friday, Saturday, Sunday) in US Eastern time
  const today = new Date();
  const eastern = new Date(today.toLocaleString('en-US', { timeZone: 'America/New_York' }));
  const dayOfWeek = eastern.getDay();
  const isDoubleTokenWeekend = dayOfWeek === 5 || dayOfWeek === 6 || dayOfWeek === 0;
  const isWackyWednesday = dayOfWeek === 3;

  const activeSeasonNum = selectedSeason ?? currentSeason;
  const playerCount = players ? players.length : 0;

  return (
    <div className="v2-page">
      <PgHeader
        title="Leaderboard"
        sub={`Season ${activeSeasonNum} · ${playerCount} tracked players`}
      />

      {/* Double Token Weekend Banner */}
      {isDoubleTokenWeekend && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.2) 0%, rgba(255, 140, 0, 0.2) 50%, rgba(232, 56, 74, 0.2) 100%)',
          padding: '16px 20px',
          borderRadius: '12px',
          border: '2px solid rgba(255, 215, 0, 0.5)',
          marginBottom: '24px',
          textAlign: 'center',
          boxShadow: '0 4px 16px rgba(255, 215, 0, 0.2)',
          animation: 'pulse 2s ease-in-out infinite'
        }}>
          <div style={{
            fontSize: '22px',
            fontWeight: 'bold',
            color: '#FFD700',
            marginBottom: '4px',
            letterSpacing: '1px'
          }}>
            DOUBLE TOKEN WEEKEND
          </div>
          <div style={{
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.85)'
          }}>
            All wins award <strong style={{ color: '#FFD700' }}>2 tokens</strong> instead of 1! (Friday - Sunday)
          </div>
        </div>
      )}

      {/* Wacky Wednesday Banner */}
      {isWackyWednesday && (
        <div style={{
          background: 'linear-gradient(135deg, rgba(160, 32, 240, 0.2) 0%, rgba(255, 20, 147, 0.2) 50%, rgba(0, 200, 255, 0.2) 100%)',
          padding: '16px 20px',
          borderRadius: '12px',
          border: '2px solid rgba(160, 32, 240, 0.5)',
          marginBottom: '24px',
          textAlign: 'center',
          boxShadow: '0 4px 16px rgba(160, 32, 240, 0.2)',
          animation: 'pulse 2s ease-in-out infinite'
        }}>
          <div style={{
            fontSize: '22px',
            fontWeight: 'bold',
            color: '#c084fc',
            marginBottom: '4px',
            letterSpacing: '1px'
          }}>
            WACKY WEDNESDAY
          </div>
          <div style={{
            fontSize: '14px',
            color: 'rgba(255, 255, 255, 0.85)'
          }}>
            Wins award a <strong style={{ color: '#c084fc' }}>random 0–10 tokens</strong> — spin the wheel!
          </div>
        </div>
      )}

      {/* Player Claiming Section - for logged-in users */}
      {user && userData && !userData.claimedPlayer && (
        <div style={{
          background: 'var(--bg-elevated)',
          padding: '16px',
          borderRadius: '12px',
          border: '1px solid var(--border)',
          marginBottom: '24px'
        }}>
          <h2 style={{ fontSize: '18px', marginBottom: '12px', fontWeight: '700' }}>
            👤 Claim Your Player
          </h2>
          <p style={{ color: 'var(--text-secondary)', fontSize: '14px', marginBottom: '12px' }}>
            Select a player below to claim them. You can only claim one player!
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
            {players.filter(p => !claimedPlayers.includes(p.id)).map(p => (
              <button
                key={p.id}
                onClick={() => handleClaimPlayer(p.id)}
                style={{
                  padding: '8px 16px',
                  background: 'var(--bg-card)',
                  border: '1px solid var(--border)',
                  borderRadius: '8px',
                  color: 'var(--text-primary)',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--red)';
                  e.currentTarget.style.background = 'rgba(232, 56, 74, 0.1)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--border)';
                  e.currentTarget.style.background = 'var(--bg-card)';
                }}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <SubTabs tabs={tabs} active={tab} onChange={setTab} />

      {/* Season Filter — hidden on richness tab (no snapshot data for net worth) */}
      {tab !== "richness" && (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
          <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Season:</span>
          {Object.keys(seasons).map(s => {
            const sNum = parseInt(s);
            const isActive = (selectedSeason ?? currentSeason) === sNum;
            return (
              <button
                key={s}
                onClick={() => setSelectedSeason(sNum)}
                style={{
                  padding: '6px 14px',
                  background: isActive ? 'rgba(232, 56, 74, 0.2)' : 'rgba(255,255,255,0.03)',
                  border: `1px solid ${isActive ? 'var(--red)' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '6px',
                  color: isActive ? 'var(--red)' : 'var(--text-secondary)',
                  fontWeight: isActive ? '700' : '500',
                  fontSize: '13px',
                  cursor: 'pointer',
                }}
              >
                {seasons[s]?.label || `Season ${s}`}
              </button>
            );
          })}
        </div>
      )}

      {tab === "richness" ? (
        // Richness Tab
        <div className="richness-leaderboard">
          {richnessLoading ? (
            <p>Loading richness data...</p>
          ) : richnessData.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">💰</div>
              <h2>No wealth data yet</h2>
              <p>Players need to claim accounts and earn tokens to appear here.</p>
            </div>
          ) : (
            <div className="lb-table-container">
              <table className="lb-table">
                <thead>
                  <tr>
                    <th className="lb-th-rank">#</th>
                    <RichnessHeader column="name" label="Name" />
                    <RichnessHeader column="liquid" label="Liquid" />
                    <RichnessHeader column="assets" label="Assets" />
                    <RichnessHeader column="items" label="Items" />
                    <RichnessHeader column="netWorth" label="Net Worth" />
                    <th className="lb-th">Rank</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedRichness.map((entry, i) => (
                    <tr key={entry.player.id} className="lb-tr" onClick={() => setSelectedPlayer(entry.player)}>
                      <td className="lb-td-rank">{i + 1}</td>
                      <td className="lb-td-name" style={{ color: entry.rank.color }}>
                        <PlayerNameWithEmoji playerName={entry.player.name} playerId={entry.player.id} playerEmojis={playerEmojis} seasonConfig={seasonConfig} />
                      </td>
                      <td className="lb-td">
                        <div>${entry.liquid.toFixed(2)}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>🔵{entry.crewmateTokens.toFixed(2)} 🔴{entry.imposterTokens.toFixed(2)}</div>
                      </td>
                      <td className="lb-td">
                        <div>${entry.assets.toFixed(2)}</div>
                        <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>
                          {Object.keys(entry.lootboxCounts).length > 0 && (
                            <span>
                              {Object.entries(entry.lootboxCounts).map(([type, count]) => (
                                <span key={type} style={{ marginRight: '4px' }}>{LOOTBOX_LABELS[type] || type}{count}</span>
                              ))}
                            </span>
                          )}
                          {entry.stocks > 0 && <span style={{ marginLeft: '4px' }}>📈{entry.stocks.toFixed(2)}</span>}
                        </div>
                      </td>
                      <td className="lb-td" style={{ color: entry.items > 0 ? 'var(--text-primary)' : 'var(--text-dim)' }}>
                        <div>{entry.items > 0 ? '$' + entry.items.toFixed(2) : '—'}</div>
                        {entry.itemCount > 0 && <div style={{ fontSize: '11px', color: 'var(--text-dim)' }}>{entry.itemCount} owned</div>}
                      </td>
                      <td className="lb-td-elo">${entry.netWorth.toFixed(2)}</td>
                      <td className="lb-td" style={{ color: entry.rank.color, fontWeight: 700, whiteSpace: 'nowrap' }}>
                        {entry.rank.name}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          <div className="richness-ranks-legend">
            <h3>Ranks</h3>
            <div className="richness-ranks-grid">
              {RICHNESS_RANKS.map((r, i) => (
                <div key={r.name} className="richness-rank-item">
                  <span className="richness-rank-name" style={{ color: r.color }}>{r.name}</span>
                  <span className="richness-rank-min">{i < RICHNESS_RANKS.length - 1 ? `$${r.min} – $${RICHNESS_RANKS[i + 1].min - 1}` : `$${r.min}+`}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      ) : tab === "gamelog" ? (
        // Game Log Tab
        <div className="game-log-section">
          {loadingGames ? (
            <p>Loading games...</p>
          ) : games.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <h2>No games logged yet</h2>
              <p>Start logging games to see the history here.</p>
            </div>
          ) : (
            <div className="games-list">
              {games.map((g) => (
                <div key={g.id} className="game-row" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                  <div className="game-info" style={{ flex: 1 }}>
                    <div className="game-outcome">
                      {g.didWin ? "✅ Impostors Won" : "❌ Impostors Lost"}
                      {g.outcomeType && (
                        <span style={{ marginLeft: '8px', fontSize: '12px', opacity: 0.7 }}>
                          ({g.outcomeType === 'tasks' ? '✅ Tasks' : g.outcomeType === 'kills' ? '🔪 Kills' : g.outcomeType === 'sabotage' ? '💣 Sabotage' : '🗳️ Votes'})
                        </span>
                      )}
                    </div>
                    <div className="game-details">
                      <span>🔴 Impostors: {g.impostors.join(", ")}</span>
                      {g.crewmates && <span>🔵 Crewmates: {g.crewmates.join(", ")}</span>}
                      <span>Players: {g.totalPlayers}</span>
                      <span>Remaining: {g.playersRemaining}</span>
                    </div>
                    <div className="game-timestamp">{formatDate(g.timestamp)}</div>
                  </div>
                  {g.eloChanges && (
                    <button
                      onClick={() => setSelectedGameForElo(g)}
                      style={{
                        padding: '8px 12px',
                        background: 'rgba(100,149,237,0.2)',
                        border: '1px solid rgba(100,149,237,0.5)',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        color: '#6495ED',
                        fontWeight: 'bold'
                      }}
                      title="View ELO details"
                    >
                      ℹ️ ELO
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : sortedPlayers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📊</div>
          <h2>Not enough data yet</h2>
          <p>Players need some activity to appear on the leaderboard.</p>
        </div>
      ) : (
        // Stats Table
        <div className="lb-table-container">
          <table className="lb-table">
            <thead>
              <tr>
                <th className="lb-th-rank">#</th>
                <TableHeader column="name" label="Name" />
                <TableHeader column="elo" label="ELO" />
                <TableHeader column="winRate" label="Win %" />
                <TableHeader column="wins" label="Wins" />
                <TableHeader column="losses" label="Losses" />
                <TableHeader column="games" label="Games" />
                <TableHeader column="streak" label="Streak" />
                <th className="lb-th">Rank</th>
              </tr>
            </thead>
            <tbody>
              {sortedPlayers.map((p, i) => {
                const rank = getRank(p.elo);
                return (
                  <tr
                    key={p.id}
                    className="lb-tr"
                    onClick={() => setSelectedPlayer(p)}
                  >
                    <td className="lb-td-rank">{i + 1}</td>
                    <td className={`lb-td-name ${rank.name === 'Among Us Lord' ? 'rainbow-text' : ''}`} style={rank.name === 'Among Us Lord' ? {} : { color: rank.color }}>
                      <PlayerNameWithEmoji playerName={p.name} playerId={p.id} playerEmojis={playerEmojis} seasonConfig={seasonConfig} />
                      {playerEmojis[p.id]?.isStatTrak && playerEmojis[p.id]?.trackedStats?.length > 0 && (
                        <div className="lb-stattrak-stats">
                          <span className="lb-stattrak-badge">ST™</span>
                          {playerEmojis[p.id].trackedStats.slice(0, 3).map((stat, idx) => (
                            <span key={stat} className="lb-stattrak-stat">
                              {idx > 0 && <span className="lb-stattrak-divider">·</span>}
                              <span className="lb-stattrak-label">{STATTRAK_LABELS[stat] || stat}</span>
                              <span className="lb-stattrak-value">{playerEmojis[p.id].statValues?.[stat] ?? 0}</span>
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="lb-td-elo">{Math.round(p.elo)}</td>
                    <td className="lb-td">{p.winRate.toFixed(1)}%</td>
                    <td className="lb-td lb-td-green">{p.wins} <span style={{ color: '#888', fontSize: '11px' }}>({p.survivedWins})</span></td>
                    <td className="lb-td lb-td-red">{p.losses} <span style={{ color: '#888', fontSize: '11px' }}>({p.survivedLosses})</span></td>
                    <td className="lb-td">{p.games}</td>
                    <td className="lb-td">
                      {p.streakType === 'win' ? (
                        <span className="streak-badge win">W{p.streak}</span>
                      ) : p.streakType === 'loss' ? (
                        <span className="streak-badge loss">L{p.streak}</span>
                      ) : (
                        <span className="streak-badge">-</span>
                      )}
                    </td>
                    <td className={`lb-td-rank-badge ${rank.name === 'Master' ? 'rank-master' : rank.name === 'Grand Master' ? 'rank-grandmaster' : rank.name === 'Among Us Lord' ? 'rank-amongus-lord' : ''}`} style={{ color: rank.color }}>
                      <img src={rank.icon} alt={rank.name} className="rank-badge-img" />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {selectedPlayer && (
        <PlayerDetailModal
          player={selectedPlayer}
          onClose={() => setSelectedPlayer(null)}
          allGames={games}
          allPlayers={players}
          playerEmojis={playerEmojis}
          user={user}
          userData={userData}
          onUserDataChange={onUserDataChange}
          seasonConfig={seasonConfig}
        />
      )}

      {selectedGameForElo && (
        <EloDetailsModal
          game={selectedGameForElo}
          players={players}
          onClose={() => setSelectedGameForElo(null)}
        />
      )}

      <div className="rank-legend">
        <h3>Rank Tiers</h3>
        <div className="rank-grid">
          {RANKS.map((r) => (
            <div key={r.name} className="rank-item" style={{ color: r.color }}>
              <img src={r.icon} alt={r.name} className="rank-badge-img" />
              <span className="rank-name">{r.name}</span>
              <span className="rank-threshold">{r.min}+</span>
            </div>
          ))}
        </div>
      </div>

      {/* Weekly Rank Rewards Info */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.1) 0%, rgba(232, 56, 74, 0.1) 100%)',
        padding: '16px 20px',
        borderRadius: '12px',
        border: '1px solid rgba(255, 215, 0, 0.3)',
        marginTop: '20px',
        boxShadow: '0 4px 12px rgba(255, 215, 0, 0.15)'
      }}>
        <h3 style={{
          fontSize: '16px',
          fontWeight: 'bold',
          marginBottom: '12px',
          color: '#FFD700',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          🏆 Weekly Rank Rewards
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '8px',
          fontSize: '14px',
          color: 'var(--text-primary)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span><strong>Among Us Lord:</strong> 2🔴 + 20🔵</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span><strong>Grand Master:</strong> 1🔴 + 10🔵</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span><strong>Master:</strong> 10🔵</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span><strong>Diamond:</strong> 5🔵</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span><strong>Gold:</strong> 3🔵</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span><strong>Silver:</strong> 1🔵</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span><strong>Bronze:</strong> Nothing</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span><strong>Copper:</strong> Nothing</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#8B4513' }}>
            <span><strong>Wood:</strong> -1🔵</span>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#8B8B8B' }}>
            <span><strong>Crewling:</strong> -5🔵</span>
          </div>
        </div>
        <div style={{
          marginTop: '12px',
          fontSize: '12px',
          color: 'rgba(255, 255, 255, 0.7)',
          fontStyle: 'italic'
        }}>
          Rewards are distributed once per week based on your current rank
        </div>
      </div>
    </div>
  );
}

export default LeaderboardPage;
